import { Controller } from "@hotwired/stimulus";

const SUB_GROUP_FIELD = "sub_group_ids_in[]";
const RESOURCE_FIELD = "resource_ids_in[]";
const UNCHECKED_PARAMS: Record<string, string> = {
  [SUB_GROUP_FIELD]: "unchecked_sub_group_ids[]",
  [RESOURCE_FIELD]: "unchecked_resource_ids[]",
};
// 期間未入力側を開いた範囲として扱うための番兵。YYYYMMDD の文字列比較で判定する
const MIN_DATE = "00000000";
const MAX_DATE = "99999999";

type DateRange = { from: string; to: string };

export default class extends Controller<HTMLElement> {
  static targets = [
    "frame",
    "startDate",
    "endDate",
    "aggregationType",
    "description",
    "siteFilter",
    "siteItem",
    "noSitesMessage",
    "subGroupFilter",
    "subGroupGroup",
    "subGroupItem",
    "noSubGroupsMessage",
    "resourceFilter",
    "resourceGroup",
    "resourceItem",
    "noResourcesMessage",
    "submitButton",
    "dateRangeMessage",
  ];
  static values = { newUrl: String, dateRangeMessage: String };

  declare readonly frameTarget: HTMLElement & { reload(): void };
  declare readonly startDateTarget: HTMLInputElement;
  declare readonly endDateTarget: HTMLInputElement;
  declare readonly dateRangeMessageTargets: HTMLElement[];
  declare readonly dateRangeMessageValue: string;
  declare readonly aggregationTypeTarget: HTMLSelectElement;
  declare readonly descriptionTargets: HTMLElement[];
  declare readonly siteFilterTargets: HTMLInputElement[];
  declare readonly siteItemTargets: HTMLElement[];
  declare readonly noSitesMessageTargets: HTMLElement[];
  declare readonly subGroupFilterTargets: HTMLInputElement[];
  declare readonly subGroupGroupTargets: HTMLElement[];
  declare readonly noSubGroupsMessageTargets: HTMLElement[];
  declare readonly resourceFilterTargets: HTMLInputElement[];
  declare readonly resourceGroupTargets: HTMLElement[];
  declare readonly noResourcesMessageTargets: HTMLElement[];
  declare readonly submitButtonTargets: HTMLButtonElement[];
  declare readonly newUrlValue: string;

  private uncheckedIds: Record<string, Set<string>> = {
    [SUB_GROUP_FIELD]: new Set<string>(),
    [RESOURCE_FIELD]: new Set<string>(),
  };
  // 検索欄は turbo-frame 内にあり再取得で空に戻るため、再取得前の入力を保持して復元する
  private filterTexts: Record<string, string> = {};

  connect(): void {
    this.updateDateRangeMessage();
    this.applySiteFilters();
    this.syncUncheckedIds();
    this.filterSubGroups();
  }

  refreshResources(): void {
    // 新期間+旧リソースの組み合わせで送信されないよう、再取得完了(onFrameRender)まで送信不可にする
    this.submitButtonTargets.forEach((button) => {
      button.disabled = true;
    });
    this.filterTexts = {
      subGroup: this.subGroupFilterTargets[0]?.value || "",
      resource: this.resourceFilterTargets[0]?.value || "",
    };

    const params = new URLSearchParams();
    new FormData(this.element as HTMLFormElement).forEach((value, key) => {
      if (key === "authenticity_token" || key in UNCHECKED_PARAMS) return;
      params.append(key, String(value));
    });
    Object.entries(UNCHECKED_PARAMS).forEach(([field, param]) => {
      this.uncheckedIds[field].forEach((id) => params.append(param, id));
    });
    const url = `${this.newUrlValue}?${params.toString()}`;

    if (this.frameTarget.getAttribute("src") === url) {
      this.frameTarget.reload();
    } else {
      this.frameTarget.setAttribute("src", url);
    }
  }

  onFrameRender(): void {
    this.subGroupFilterTargets.forEach((input) => (input.value = this.filterTexts.subGroup || ""));
    this.resourceFilterTargets.forEach((input) => (input.value = this.filterTexts.resource || ""));
    this.filterSubGroups();
  }

  switchDescription(): void {
    const selected = this.aggregationTypeTarget.value;
    this.descriptionTargets.forEach((el) => {
      el.classList.toggle("hidden", el.dataset.aggregationType !== selected);
    });
  }

  // --- 出力期間・現場 ---

  onPeriodChange(): void {
    this.updateDateRangeMessage();
    this.applySiteFilters();
    this.refreshResources();
  }

  onSiteChange(): void {
    this.refreshResources();
  }

  filterSites(): void {
    const before = this.selectedSiteIdsKey();
    this.applySiteFilters();
    if (this.selectedSiteIdsKey() !== before) this.refreshResources();
  }

  private applySiteFilters(): void {
    if (this.siteItemTargets.length === 0) return;

    const text = (this.siteFilterTargets[0]?.value || "").toLowerCase();
    const period = this.currentPeriod();

    this.siteItemTargets.forEach((item) => {
      const visible =
        this.overlapsPeriod(this.parseRanges(item.dataset.planRanges), period) && this.matchesSearchText(item, text);
      this.setElementHidden(item, !visible);
      this.setCheckboxesDisabled(item, !visible);
    });

    this.updateEmptyMessage(this.noSitesMessageTargets, this.siteItemTargets);
  }

  private selectedSiteIdsKey(): string {
    return Array.from(this.element.querySelectorAll<HTMLInputElement>(`input[name='site_ids_in[]']:checked:not(:disabled)`))
      .map((checkbox) => checkbox.value)
      .join(",");
  }

  private currentPeriod(): DateRange {
    return {
      from: this.startDateTarget.value.replace(/-/g, "") || MIN_DATE,
      to: this.endDateTarget.value.replace(/-/g, "") || MAX_DATE,
    };
  }

  // "YYYYMMDD-YYYYMMDD,..." (Users::DataExportFiltersForm#plan_ranges_for_site)
  private parseRanges(text: string | undefined): DateRange[] {
    if (!text) return [];

    return text.split(",").flatMap((part) => {
      const [from, to] = part.split("-");
      return from && to ? [{ from, to }] : [];
    });
  }

  private overlapsPeriod(ranges: DateRange[], period: DateRange): boolean {
    return ranges.some((range) => range.from <= period.to && range.to >= period.from);
  }

  // --- 分類 ---

  filterSubGroups(): void {
    const text = (this.subGroupFilterTargets[0]?.value || "").toLowerCase();

    this.subGroupGroupTargets.forEach((group) => {
      const groupMatches = this.matchesSearchText(group, text);
      let groupHasVisible = false;

      this.itemsOf(group, "subGroupItem").forEach((item) => {
        const visible = !text || groupMatches || this.matchesSearchText(item, text);
        this.setElementHidden(item, !visible);
        this.setCheckboxesDisabled(item, !visible);
        if (visible) groupHasVisible = true;
      });

      this.setElementHidden(group, !groupHasVisible);
    });

    this.updateEmptyMessage(this.noSubGroupsMessageTargets, this.subGroupGroupTargets);
    this.filterResources();
  }

  onSubGroupChange(): void {
    this.syncUncheckedIds();
    this.filterResources();
  }

  toggleGroupChildren(event: Event): void {
    const parent = event.target as HTMLInputElement;
    const group = parent.closest<HTMLElement>("[data-data-export-form-target='subGroupGroup']");
    if (!group) return;

    this.itemsOf(group, "subGroupItem").forEach((item) => {
      if (this.isHidden(item)) return;
      item.querySelectorAll<HTMLInputElement>("input[type='checkbox']").forEach((checkbox) => {
        checkbox.checked = parent.checked;
      });
    });
    this.onSubGroupChange();
  }

  updateGroupParent(event: Event): void {
    const group = (event.target as HTMLInputElement).closest<HTMLElement>("[data-data-export-form-target='subGroupGroup']");
    if (!group) return;

    const visibleCheckboxes = this.itemsOf(group, "subGroupItem")
      .filter((item) => !this.isHidden(item))
      .flatMap((item) => Array.from(item.querySelectorAll<HTMLInputElement>("input[type='checkbox']")));
    const parent = group.querySelector<HTMLInputElement>(":scope > label input[type='checkbox']");
    if (parent) parent.checked = visibleCheckboxes.length > 0 && visibleCheckboxes.every((checkbox) => checkbox.checked);
    this.onSubGroupChange();
  }

  // --- リソース ---

  filterResources(): void {
    const text = (this.resourceFilterTargets[0]?.value || "").toLowerCase();
    const selectedSubGroupIds = new Set(
      Array.from(this.element.querySelectorAll<HTMLInputElement>(`input[name='${SUB_GROUP_FIELD}']:checked:not(:disabled)`)).map(
        (checkbox) => checkbox.value,
      ),
    );

    this.resourceGroupTargets.forEach((group) => {
      const subGroupSelected = selectedSubGroupIds.has(group.dataset.subGroupId || "");
      const groupMatches = this.matchesSearchText(group, text);
      let groupHasVisible = false;

      this.itemsOf(group, "resourceItem").forEach((item) => {
        const visible = subGroupSelected && (!text || groupMatches || this.matchesSearchText(item, text));
        this.setElementHidden(item, !visible);
        this.setCheckboxesDisabled(item, !visible);
        if (visible) groupHasVisible = true;
      });

      this.setElementHidden(group, !groupHasVisible);
    });

    this.updateEmptyMessage(this.noResourcesMessageTargets, this.resourceGroupTargets);
    this.updateSubmitState();
  }

  onResourceChange(): void {
    this.syncUncheckedIds();
    this.updateSubmitState();
  }

  // --- 共通 ---

  // 要素の表示切替専用。
  private setElementHidden(el: HTMLElement, hidden: boolean): void {
    el.style.display = hidden ? "none" : "";
  }

  // 要素配下のcheckboxのdisabledを切り替える専用(送信対象から外す/戻す)。
  // チェック状態は保持し、再表示時に選択状態を復元できるようにする
  private setCheckboxesDisabled(el: HTMLElement, hidden: boolean): void {
    el.querySelectorAll<HTMLInputElement>("input[type='checkbox']").forEach((checkbox) => {
      checkbox.disabled = hidden;
    });
  }

  // 非表示や現在の選択肢に無いもののエントリは触らないので、候補から外れて再び戻ってきたものも外れたままになる
  private syncUncheckedIds(): void {
    Object.keys(this.uncheckedIds).forEach((field) => {
      this.element
        .querySelectorAll<HTMLInputElement>(`input[name='${field}'][type='checkbox']:not(:disabled)`)
        .forEach((checkbox) => {
          if (checkbox.checked) {
            this.uncheckedIds[field].delete(checkbox.value);
          } else {
            this.uncheckedIds[field].add(checkbox.value);
          }
        });
    });
  }

  private updateSubmitState(): void {
    const hasChecked = this.element.querySelector(`input[name='${RESOURCE_FIELD}']:checked:not(:disabled)`) !== null;
    this.submitButtonTargets.forEach((button) => {
      button.disabled = !hasChecked || this.isDateRangeInvalid();
    });
  }

  private isDateRangeInvalid(): boolean {
    const start = this.startDateTarget.value;
    const end = this.endDateTarget.value;
    return start !== "" && end !== "" && start > end;
  }

  private updateDateRangeMessage(): void {
    const invalid = this.isDateRangeInvalid();
    this.dateRangeMessageTargets.forEach((message) => {
      message.textContent = invalid ? this.dateRangeMessageValue : "";
      message.classList.toggle("hidden", !invalid);
    });
    this.updateSubmitState();
  }

  private updateEmptyMessage(messageTargets: HTMLElement[], items: HTMLElement[]): void {
    const anyVisible = items.some((item) => !this.isHidden(item));
    messageTargets.forEach((msg) => msg.classList.toggle("hidden", anyVisible));
  }

  private itemsOf(group: HTMLElement, target: "subGroupItem" | "resourceItem"): HTMLElement[] {
    return Array.from(group.querySelectorAll<HTMLElement>(`[data-data-export-form-target='${target}']`));
  }

  private matchesSearchText(el: HTMLElement, text: string): boolean {
    return (el.dataset.searchText || "").toLowerCase().includes(text);
  }

  private isHidden(el: HTMLElement): boolean {
    return el.style.display === "none";
  }
}
